import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Search,
  Folder,
  FileText,
  ChevronRight,
  Loader2,
  LayoutGrid,
  List,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { erpKnowledgeBaseService } from '@/services/erpKnowledgeBaseService';
import { getImageURL } from '@/utils/ERPUtils';

import { logger } from '@/utils/logger';
interface BreadcrumbItem {
  id: string;
  name: string;
  type: 'root' | 'section' | 'category' | 'subcategory';
}

interface FolderItem {
  id: string;
  name: string;
  description?: string;
  children_count?: number;
}

interface DocumentItem {
  id: string;
  name: string;
  title?: string;
  document_id?: string;
  document_name?: string;
  file_url?: string;
  document_link?: string;
  description?: string;
  content?: string;
  publish?: number;
}

export const MobileKnowledge: React.FC = () => {
  const { toast } = useToast();

  // View mode state
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Navigation state - breadcrumb path
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([
    { id: 'root', name: 'Documents', type: 'root' },
  ]);

  // Data state
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);

  // Loading state
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Pagination state
  const [paginationInfo, setPaginationInfo] = useState<{
    total_count: number;
    total_pages: number;
    has_next: boolean;
    page: number;
  } | null>(null);

  // Get current level from breadcrumb
  const currentLevel = breadcrumb[breadcrumb.length - 1];

  // Helper to parse folder contents and separate folders from files
  const parseFolderContents = useCallback((response: any[], _parentId: string) => {
    const items = response || [];
    const folderItems = items
      .filter((item: any) => item.is_folder === 1)
      .map((item: any) => ({
        id: item.name || '',
        name: item.document_name || item.name || '',
        description: item.description || '',
        children_count: item.children_count || 0,
      }));
    const fileItems = items
      .filter((item: any) => item.is_file === 1)
      .map((item: any) => ({
        id: item.name || '',
        name: item.document_name || item.name || '',
        title: item.document_name || item.name || '',
        document_id: item.name || '',
        document_name: item.document_name || item.name || '',
        file_url: item.attachment || '',
        document_link: item.attachment || '',
        description: item.description || '',
      }));
    return { folders: folderItems, files: fileItems };
  }, []);

  // Fetch folder contents with pagination support
  const fetchFolderContents = useCallback(async (folderId?: string, page = 1) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      const result = await erpKnowledgeBaseService.getFolderContents(folderId, true, page, 20);
      const { folders: folderItems, files } = parseFolderContents(result.data, folderId || '');

      if (page === 1) {
        setFolders(folderItems);
        setDocuments(files);
      } else {
        // Append to existing data
        setFolders(prev => [...prev, ...folderItems]);
        setDocuments(prev => [...prev, ...files]);
      }
      setPaginationInfo(result.pagination ? {
        total_count: result.pagination.total_count,
        total_pages: result.pagination.total_pages,
        has_next: result.pagination.has_next,
        page: result.pagination.page,
      } : null);
    } catch (error) {
      logger.error('Error fetching folder contents:', error);
      toast({
        title: 'Error',
        description: 'Failed to load folder contents',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [toast, parseFolderContents]);

  // Initial load - fetch root level
  useEffect(() => {
    fetchFolderContents();
  }, [fetchFolderContents]);

  // Navigate to a folder
  const navigateToFolder = useCallback((item: FolderItem, type: 'section' | 'category' | 'subcategory') => {
    const newBreadcrumb: BreadcrumbItem = { id: item.id, name: item.name, type };
    setBreadcrumb((prev) => [...prev, newBreadcrumb]);
    setPaginationInfo(null);
    fetchFolderContents(item.id);
  }, [fetchFolderContents]);

  // Navigate to a breadcrumb item
  const navigateToBreadcrumb = useCallback((index: number) => {
    const targetItem = breadcrumb[index];
    setBreadcrumb(breadcrumb.slice(0, index + 1));
    setPaginationInfo(null);

    if (targetItem.type === 'root') {
      fetchFolderContents();
    } else {
      fetchFolderContents(targetItem.id);
    }
  }, [breadcrumb, fetchFolderContents]);

  // Helper to map search API response to display items
  const mapSearchResults = useCallback((data: any[]) => {
    return (data || []).map((item: any) => ({
      id: item.name || '',
      name: item.document_name || item.name || '',
      type: item.is_file === 1 ? 'document' : 'folder',
      isDocument: item.is_file === 1,
      document: item.is_file === 1 ? {
        id: item.name || '',
        name: item.document_name || item.name || '',
        title: item.document_name || item.name || '',
        document_id: item.name || '',
        document_name: item.document_name || item.name || '',
        file_url: item.attachment || '',
        document_link: item.attachment || '',
        description: item.description || '',
      } : null,
      parent_directory: item.parent_directory || '',
    }));
  }, []);

  // Fetch search results with pagination support
  const fetchSearchResults = useCallback(async (query: string, page = 1) => {
    if (page === 1) {
      setIsSearching(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const result = await erpKnowledgeBaseService.searchItems(query, undefined, true, page);
      const results = mapSearchResults(result.data);
      if (page === 1) {
        setSearchResults(results);
      } else {
        setSearchResults(prev => [...prev, ...results]);
      }
      setPaginationInfo(result.pagination ? {
        total_count: result.pagination.total_count,
        total_pages: result.pagination.total_pages,
        has_next: result.pagination.has_next,
        page: result.pagination.page,
      } : null);
    } catch (error) {
      logger.error('Search failed:', error);
      toast({
        title: 'Error',
        description: 'Search failed',
        variant: 'destructive',
      });
    } finally {
      setIsSearching(false);
      setLoadingMore(false);
    }
  }, [toast, mapSearchResults]);

  // Debounced search handler
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If query is empty, clear search results
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // Debounce API call (300ms)
    searchTimeoutRef.current = setTimeout(() => {
      fetchSearchResults(query, 1);
    }, 300);
  }, [fetchSearchResults]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Infinite scroll - observe a sentinel element at the bottom
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && paginationInfo?.has_next && !loading && !loadingMore) {
          if (searchQuery.trim()) {
            fetchSearchResults(searchQuery, paginationInfo.page + 1);
          } else {
            const currentFolderId = currentLevel.type === 'root' ? undefined : currentLevel.id;
            fetchFolderContents(currentFolderId, paginationInfo.page + 1);
          }
        }
      },
      { root: scrollContainerRef.current, threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [paginationInfo, loading, loadingMore, currentLevel, searchQuery, fetchFolderContents, fetchSearchResults]);

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
  };

  // Handle open document - directly open the file
  const openDocument = (doc: DocumentItem) => {
    const documentLink = doc.document_link || doc.file_url;
    if (!documentLink) {
      toast({ title: 'No attachment', description: 'This document has no file attached.' });
      return;
    }
    try {
      const fileUrl = getImageURL(documentLink);
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      logger.error('Open failed', err);
      toast({ title: 'Failed to open', description: err.message || 'Could not open document.', variant: 'destructive' });
    }
  };

  // Determine what type of folder navigation based on current level
  const getNextFolderType = (): 'section' | 'category' | 'subcategory' => {
    if (currentLevel.type === 'root') return 'section';
    if (currentLevel.type === 'section') return 'category';
    return 'subcategory';
  };

  // Get items to display based on search
  const displayItems = useMemo(() => {
    if (searchQuery.trim()) {
      return searchResults;
    }
    // Combine folders and documents for display
    const folderItems = folders.map(f => ({
      id: f.id,
      name: f.name,
      type: 'folder' as const,
      isDocument: false,
      document: null as DocumentItem | null,
      children_count: f.children_count,
    }));
    const docItems = documents.map(d => ({
      id: d.id,
      name: d.name,
      type: 'document' as const,
      isDocument: true,
      document: d,
    }));
    return [...folderItems, ...docItems];
  }, [searchQuery, searchResults, folders, documents]);

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header Section */}
      <div className="flex-shrink-0 px-4 pt-6 pb-4">
        {/* Title and Upload Button */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold text-foreground">
            Documents
          </h1>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search documents and folders..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-10 bg-background border-border text-foreground placeholder:text-muted-foreground rounded-lg"
          />
          {isSearching && (
            <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />
          )}
        </div>

        {/* Breadcrumb Navigation - Mobile friendly with ... */}
        {!searchQuery.trim() && (
          <div className="flex items-center justify-between gap-2 mb-2">
            <nav className="flex items-center text-sm flex-1 min-w-0 overflow-hidden">
              {/* Show ... and last 2 items when path is long */}
              {breadcrumb.length > 2 ? (
                <>
                  <button
                    onClick={() => navigateToBreadcrumb(0)}
                    className="text-muted-foreground hover:text-foreground flex-shrink-0"
                  >
                    ...
                  </button>
                  <ChevronRight className="h-4 w-4 mx-1 text-muted-foreground flex-shrink-0" />
                  <button
                    onClick={() => navigateToBreadcrumb(breadcrumb.length - 2)}
                    className="text-muted-foreground hover:underline truncate max-w-[80px] flex-shrink-0"
                  >
                    {breadcrumb[breadcrumb.length - 2].name}
                  </button>
                  <ChevronRight className="h-4 w-4 mx-1 text-muted-foreground flex-shrink-0" />
                  <span className="text-foreground font-medium truncate min-w-0">
                    {currentLevel.name}
                  </span>
                </>
              ) : (
                /* Show full breadcrumb when short */
                breadcrumb.map((item, index) => (
                  <span key={item.id + index} className="flex items-center min-w-0 flex-shrink-0 last:flex-shrink">
                    {index > 0 && <ChevronRight className="h-4 w-4 mx-1 text-muted-foreground flex-shrink-0" />}
                    <button
                      onClick={() => navigateToBreadcrumb(index)}
                      className={cn(
                        'hover:underline truncate',
                        index === breadcrumb.length - 1
                          ? 'text-foreground font-medium'
                          : 'text-muted-foreground'
                      )}
                    >
                      {item.name}
                    </button>
                  </span>
                ))
              )}
            </nav>

            {/* View Toggle */}
            <div className="flex items-center border border-border rounded-md overflow-hidden flex-shrink-0">
              <button
                onClick={() => setViewMode('grid')}
                className={cn(
                  'p-2 transition-colors',
                  viewMode === 'grid'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground'
                )}
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={cn(
                  'p-2 transition-colors',
                  viewMode === 'list'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground'
                )}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Search results header */}
        {searchQuery.trim() && (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSearch}
              className="text-muted-foreground"
            >
              Clear search
            </Button>
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto px-4 pb-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-foreground" />
          </div>
        ) : displayItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <FileText className="h-16 w-16 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center mb-2">
              {searchQuery.trim() ? 'No results found' : 'No items found'}
            </p>
            <p className="text-muted-foreground text-sm text-center">
              {searchQuery.trim() ? 'Try different search terms' : 'This folder is empty'}
            </p>
          </div>
        ) : viewMode === 'list' ? (
          /* List View */
          <div className="space-y-3">
            {displayItems.map((item) => (
              item.isDocument && item.document ? (
                /* Document Item - Click to open directly */
                <div
                  key={item.id}
                  className="p-4 bg-muted rounded-lg cursor-pointer active:bg-accent transition-colors"
                  onClick={() => openDocument(item.document!)}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-foreground truncate block">{item.name}</span>
                      {item.document?.description && (
                        <p className="text-sm text-muted-foreground truncate">{item.document.description}</p>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                  </div>
                </div>
              ) : (
                /* Folder Item */
                <div
                  key={item.id}
                  className="p-4 bg-muted rounded-lg cursor-pointer active:bg-accent transition-colors"
                  onClick={() => {
                    // Clear search when clicking a folder from search results
                    if (searchQuery.trim()) {
                      handleClearSearch();
                    }
                    navigateToFolder(
                      { id: item.id, name: item.name, children_count: (item as any).children_count },
                      getNextFolderType()
                    );
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Folder className="h-5 w-5 text-amber-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-foreground font-semibold truncate">{item.name}</h3>
                        {(item as any).children_count !== undefined && (
                          <p className="text-sm text-muted-foreground">{(item as any).children_count} items</p>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 ml-2" />
                  </div>
                </div>
              )
            ))}
          </div>
        ) : (
          /* Grid View */
          <div className="grid grid-cols-2 gap-3">
            {displayItems.map((item) => (
              item.isDocument && item.document ? (
                /* Document Card - Click to open directly */
                <div
                  key={item.id}
                  className="p-4 bg-muted rounded-lg cursor-pointer active:bg-accent transition-colors"
                  onClick={() => openDocument(item.document!)}
                >
                  <div className="flex flex-col items-center text-center">
                    <FileText className="h-10 w-10 text-blue-500 mb-2" />
                    <span className="text-sm font-medium text-foreground line-clamp-2">{item.name}</span>
                  </div>
                </div>
              ) : (
                /* Folder Card */
                <div
                  key={item.id}
                  className="p-4 bg-muted rounded-lg cursor-pointer active:bg-accent transition-colors"
                  onClick={() => {
                    if (searchQuery.trim()) {
                      handleClearSearch();
                    }
                    navigateToFolder(
                      { id: item.id, name: item.name, children_count: (item as any).children_count },
                      getNextFolderType()
                    );
                  }}
                >
                  <div className="flex flex-col items-center text-center">
                    <Folder className="h-10 w-10 text-amber-500 mb-2" />
                    <span className="text-sm font-medium text-foreground line-clamp-2">{item.name}</span>
                    {(item as any).children_count !== undefined && (
                      <span className="text-xs text-muted-foreground mt-1">{(item as any).children_count} items</span>
                    )}
                  </div>
                </div>
              )
            ))}
          </div>
        )}

        {/* Infinite scroll sentinel + loading indicator */}
        {!loading && paginationInfo?.has_next && (
          <div ref={sentinelRef} className="flex justify-center py-4">
            {loadingMore && (
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
